// @ts-strict-ignore
import * as React from 'react';
import Icon, { BranchesOutlined, PullRequestOutlined } from '@ant-design/icons';
import {
    CodeType,
    CommitItem,
    FeatureItemCodeStatus,
    PRItem,
    PullRequest,
    PullRequestStackInfo,
    PullRequestStatus,
    ResolvedFeatureAffiliation,
    mapCodeStatus,
} from '../../types';
import MergeIcon from '../icons/MergeIcon';

export const CHERRY_PICK_QUEUE_STATUSES = [
    FeatureItemCodeStatus.CherryPickOpen,
    FeatureItemCodeStatus.CherryPickFailed,
    FeatureItemCodeStatus.CherryPickPending,
    FeatureItemCodeStatus.CherryPickQueued,
];

export const isInCherryPickQueue = (code?: PRItem | CommitItem) =>
    !!code && CHERRY_PICK_QUEUE_STATUSES.includes(mapCodeStatus(code.status));

export const isOpenBackmergePR = (code: PRItem | CommitItem | undefined, openBackmergePRs: PullRequest[] = []) =>
    !!code && !isCommit(code) && openBackmergePRs.some((pr) => String(pr.number) === String(code.identifier));

export interface QueueLink {
    tooltip: string;
    onOpen: () => void;
}

export const stackInfoTooltipText: (stackInfo: PullRequestStackInfo, targetedBranch?: string) => string = (
    stackInfo,
    targetedBranch,
) => {
    return (
        `Part of a stack of ${stackInfo.size} pull requests (this one is #${stackInfo.position} from the bottom) targeting the ${stackInfo.baseBranch} branch` +
        (targetedBranch ? `. It targets ${targetedBranch}, the branch below it in the stack.` : '')
    );
};

export const isCommit = (code: PRItem | CommitItem): code is CommitItem => {
    if (!code) return null;
    return (code as CommitItem).type === CodeType.Commit;
};

export const featureAffiliationsFromCode: (code: PRItem | CommitItem) => ResolvedFeatureAffiliation[] = (code) => {
    if (isCommit(code)) {
        if (code.sourcePR?.featureAffiliations) {
            return code.sourcePR.featureAffiliations;
        }
        if (code.unsquashedChildCommitForPR?.featureAffiliations) {
            return code.unsquashedChildCommitForPR.featureAffiliations;
        }
    } else {
        if (code.featureAffiliations) {
            return code.featureAffiliations;
        }
    }
    return [];
};

export const iconForStatus: (status: FeatureItemCodeStatus, codeItem: PRItem | CommitItem) => React.ReactNode = (
    status: FeatureItemCodeStatus,
    codeItem: PRItem | CommitItem,
) => {
    const MergeIconSized = () => {
        return <MergeIcon width={14} height={14} />;
    };

    switch (status) {
        case FeatureItemCodeStatus.Merged:
        case FeatureItemCodeStatus.CherryPickMerged:
        case FeatureItemCodeStatus.CherryPickSkipped:
        case FeatureItemCodeStatus.CherryPickManuallyResolved:
            return <Icon component={MergeIconSized} />;
        case FeatureItemCodeStatus.OpenPR:
        case FeatureItemCodeStatus.CherryPickOpen:
        case FeatureItemCodeStatus.CherryPickClosed:
            return <PullRequestOutlined />;
        case FeatureItemCodeStatus.Unmerged:
        case FeatureItemCodeStatus.CherryPickPending:
        case FeatureItemCodeStatus.CherryPickQueued:
            return <BranchesOutlined />;
        case FeatureItemCodeStatus.CherryPickFailed:
            if (
                (codeItem as PRItem)?.pullRequestStatus &&
                (codeItem as PRItem).pullRequestStatus === PullRequestStatus.Open
            ) {
                return <PullRequestOutlined />;
            } else {
                return <BranchesOutlined />;
            }
        default:
            return null;
    }
};

export const getLabelsFromFeatureAffiliations: (
    featureAffiliations: ResolvedFeatureAffiliation[],
) => ResolvedFeatureAffiliation[] = (featureAffiliations) => {
    return featureAffiliations.filter((fa) => fa.affiliationType.indexOf('label') !== -1);
};
